/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Строка запроса</h2>
    <p className={styles.description}>
      В строке запроса можно указывать дополнительную информацию о текущем маршруте. Например, если мы просматриваем
      страницу новостных статей, маршрут может выглядеть так.{' '}
    </p>
    <CopyBlock text={`https://app.com/articles`} language="javascript" showLineNumbers={false} theme={ocean} />
    <p className={styles.description}>
      Если есть категории выбора статей по жанру и времени издания, храним выбранные опции в строке запроса.{' '}
    </p>
    <CopyBlock
      text={`https://app.com/articles?category=health&sortBy=latest`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Строка запроса содержит 2 параметра: название категории и порядок сортировки статей. При переходе по такому
      маршруту, можно сделать HTTP-запрос на бэкенд, получив только статьи необходимой категории, и отсортировать их по
      полю даты издания.{' '}
    </p>
    <p className={styles.description}>
      Текущее значение строки запроса хранится в пропе <code className={styles.code}>location</code>.{' '}
    </p>
    <CopyBlock
      text={`{
  key: 'ac3df4',
  pathname: '/articles',
  search: '?category=health&sortBy=latest',
  hash: ''
}`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      <a
        className={styles.link}
        href="https://reacttraining.com/react-router/web/api/location"
        target="_blank"
        rel="noopener noreferrer"
      >
        Документация location
      </a>
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Извлечение параметров</h3>
      <p className={styles.description}>
        Чтобы получить значения параметров, можно использовать возможности нативного класса{' '}
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams"
          target="_blank"
          rel="noopener noreferrer"
        >
          URLSearchParams
        </a>
        , а также библиотеки{' '}
        <a className={styles.link} href="https://www.npmjs.com/package/qs" target="_blank" rel="noopener noreferrer">
          qs
        </a>{' '}
        или{' '}
        <a
          className={styles.link}
          href="https://www.npmjs.com/package/query-string"
          target="_blank"
          rel="noopener noreferrer"
        >
          query-string
        </a>
        . Любой метод позволит сделать парс строки запроса и получить объект с парами{' '}
        <code className={styles.code}>ключ:значение</code>.
      </p>
      <CopyBlock
        text={`import queryString from 'query-string';

const queryParams = queryString.parse(props.location.search);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Изменение параметров</h3>
      <p className={styles.description}>
        Допустим, для выбора категории статей используется <code className={styles.code}>{`<select>`}</code>. При выборе
        опции необходимо обновлять URL используя метод <code className={styles.code}>history.push()</code> для
        добавления новой записи в журнал истории.{' '}
      </p>
      <p className={styles.description}>
        Берем текущее значение <code className={styles.code}>location.pathname</code> и обновляем{' '}
        <code className={styles.code}>search</code>.{' '}
      </p>
      <CopyBlock
        text={`onCategoryChange = category => {
  this.props.history.push({
    pathname: this.props.location.pathname,
    search: \`category=$ {category}\`,
  });
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Отслеживание изменений</h3>
      <p className={styles.description}>
        Если меняется строка запроса, компоненту пробрасываются новые пропсы, и в методе{' '}
        <code className={styles.code}>componentDidUpdate()</code> можно проверить изменилась ли категория и порядок
        сортировки. Если изменились, делаем HTTP-запрос или сортируем текущие статьи.{' '}
      </p>
      <CopyBlock
        text={`const getCategoryFromProps = props => queryString.parse(props.location.search).category;

componentDidUpdate(prevProps) {
  const prevCategory = getCategoryFromProps(prevProps);
  const nextCategory = getCategoryFromProps(this.props);

  if (prevCategory !== nextCategory) {
    this.fetchArticles(nextCategory);
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
